import { useOutlookActions } from "@/pages/taskpane/lib/office/hooks";
import { OrderService } from "@/pages/taskpane/lib/order/service/OrderService";
import {
  IDialogMessageBase,
  IOpenOrderRequest,
  IRequestMeetingType,
  ISavePlaceOrderSessionDataRequest,
} from "@/pages/taskpane/lib/order/types/OrderEntity";
import {
  DialogErrorCode,
  DialogEventType,
  EOfficeDialogMessageType,
  IDialogModel,
} from "@/pages/taskpane/lib/utils/dialog";
import { usePreSelectedResourceStore } from "@/pages/taskpane/shared/stores/preSelectedResourceStore";
import { EPreSelectedResource } from "@/pages/taskpane/shared/types/enums/preSelectedResourceEnum";
import { PreSelectedResourceInfo } from "@/pages/taskpane/shared/types/preSelectedResource";
import { useMemo, useRef } from "react";
import { useResourceService } from "../../resources/services/resourceService";
import { saveMeetingType } from "../../resources/services/useSaveMeetingType";
import { useResourceStore } from "../../resources/store/resourceStore";
import { routes } from "@/pages/taskpane/shared/constants/routes";
import { useLocation, useNavigate } from "react-router-dom";

export const useOpenOrder = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const orderService = useMemo(() => new OrderService(), []);
  const { removeItem, items, updateItem } = usePreSelectedResourceStore();
  const { selectedResource, addSelectedResource, setMeetingLabel, updateResourceIsOrdered } = useResourceStore();
  const { AddResources, saveSessionId, UpdateAttendees } = useOutlookActions();
  const { removeMultipleResources } = useResourceService();
  const deletedItems = useRef<Record<string, boolean>>({});

  const savePlaceOrderSessionData = async (request: ISavePlaceOrderSessionDataRequest) => {
    const response = await orderService.savePlaceOrderSessionData(request);
    return response;
  };

  const handleUpdateAttendees = (
    attendees: { Email: string; Name: string }[],
    attendeesFromStore: { Email: string; Name: string; IsRequired?: boolean }[],
  ) => {
    const attendeeForAdd = attendees?.filter(
      (attendee) =>
        !attendeesFromStore?.some(
          (a) =>
            a.Email.toLowerCase() === attendee.Email.toLowerCase()
        )
    )
    const attendeeForRemove = attendeesFromStore?.filter(
      (attendee) =>
        !attendees?.some(
          (a) =>
            a.Email.toLowerCase() === attendee.Email.toLowerCase()
        )
    );
    if(!attendeeForAdd.length && !attendeeForRemove.length) {
      return;
    }
    UpdateAttendees(attendeeForAdd.map((attendee) => ({ Email: attendee.Email, Name: attendee.Name, IsRequired: true })), attendeeForRemove.map((attendee) => ({ Email: attendee.Email, Name: attendee.Name, IsRequired: true })));
  };

  const updateResourcePending = (item: PreSelectedResourceInfo) => {
    if (item.isResourcePending) {
      const itemMeetingType = items?.find((i) => i.isResourcePending && i.type === EPreSelectedResource.MeetingType);
      if (itemMeetingType) {
        return;
      }
      const rsIds = selectedResource.map((r) => r.id);
      if (!rsIds.includes(item.resources[0].id)) {
        addSelectedResource(item.resources[0]);
        AddResources([item.resources[0]]);
      }
    }
  };

  const updateItemCallback = (item: PreSelectedResourceInfo) => {
    if(deletedItems.current[item.id]) {
      return;
    }
    updateResourcePending(item);
    removeItem(item);
    deletedItems.current[item.id] = true;
  };

  const updateIsOrdered = (item: PreSelectedResourceInfo, sessionId: string) => {
    saveSessionId(item.resources[0].email?.toLowerCase(), `${sessionId}_save_ordered`);
    updateResourceIsOrdered(item.resources[0].email?.toLowerCase(), true);
  };

  const openPlaceOrder = async (
    requestSaveSession: ISavePlaceOrderSessionDataRequest,
    request: IOpenOrderRequest,
    item: PreSelectedResourceInfo,
    requestMeetingType?: IRequestMeetingType
  ) => {
    const saveSessionResponse = await savePlaceOrderSessionData(requestSaveSession);
    if (!saveSessionResponse.Success) {
      return;
    }
    orderService.openOrder(request, (dialog: IDialogModel) => {
      saveSessionId(item.resources[0].email?.toLowerCase(), requestSaveSession.SessionId);
      dialog.addEventHandler(
        DialogEventType.DialogEventReceived,
        (args: { message: string; origin: string | undefined } | { error: number }) => {
          if ((args as { error: number }).error === DialogErrorCode.Closed) {
            updateItemCallback(item);
            console.log("Order dialog closed");
          }
        }
      );
      dialog.addEventHandler(
        DialogEventType.DialogMessageReceived,
        (args: { message: string; origin: string | undefined } | { error: number }) => {
          //console.log(args, "Message from child");
          if ((args as { message: string }).message) {
            const message = JSON.parse((args as { message: string }).message) as IDialogMessageBase;
            switch (message.MessageType) {
              case EOfficeDialogMessageType.SavedReservations: {
                updateItemCallback(item);
                updateIsOrdered(item, `${requestSaveSession.SessionId}_save_ordered`);
                const meetingTypeId = Number(message?.MeetingType?.Value);
                if (
                  requestMeetingType?.meetingLabelSelected !== undefined &&
                  meetingTypeId &&
                  meetingTypeId !== requestMeetingType?.meetingLabelSelected &&
                  requestMeetingType?.meetingLabelOptions
                ) {
                  saveMeetingType({
                    uid: request.CustomUid,
                    resourcesIds: request.SelectedResources,
                    // originalResourceIds: selectedResource.map((item) => item.id).join(","),
                    meetingType:
                      requestMeetingType?.meetingLabelOptions.find((item) => item.id === meetingTypeId)?.name || "",
                    isRecurring: request.IsRecurringApp === "1" ? 1 : 0,
                    isNewMeeting: !!requestMeetingType?.isNewMeeting,
                    timeZoneName: request.TimeZoneCode || "",
                    organizerName: request.OrganizerEmail || "",
                    organizerEmail: request.OrganizerEmail || "",
                    startDate: requestMeetingType?.startDate || "",
                    endDate: requestMeetingType?.endDate || "",
                    globalId: request.GlobalId || "",
                    isSendMail: false,
                    subject: request.Subject || "",
                  });
                  setMeetingLabel(meetingTypeId);
                }
                break;
              }
              // eslint-disable-next-line no-case-declarations
              case EOfficeDialogMessageType.CloseDialog: {
                updateItemCallback(item);
                dialog.close();
                break;
              }
              case EOfficeDialogMessageType.UpdateAttendees: {
                updateItemCallback(item);
                updateIsOrdered(item, `${requestSaveSession.SessionId}_save_ordered`);
                const attendees = (message?.Attendees || []).filter(attendee => attendee.Email.toLowerCase() !== request.OrganizerEmail?.toLowerCase());
                const attendeesFromStore = requestSaveSession.Attendees.filter(attendee => attendee.Email.toLowerCase() !== request.OrganizerEmail?.toLowerCase());
                const isNotChanged = attendees.length === attendeesFromStore.length && attendees.every(attendee => attendeesFromStore.some(a => a.Email.toLowerCase() === attendee.Email.toLowerCase()));
                if((!attendees.length && !attendeesFromStore.length) || isNotChanged) {
                  break;
                }
                setTimeout(() => {
                  handleUpdateAttendees(attendees, attendeesFromStore);
                }, 1000);
                break;
              }
            }
          }
        }
      );
    });
  };

  const onHandleCancel = (notifyInfo: PreSelectedResourceInfo) => {
    if (notifyInfo && notifyInfo.resources.length > 0) {
      const notifyMeetingType = items?.find((i) => i.type === EPreSelectedResource.MeetingType);
      if (selectedResource.length === 0) {
        const itemsPendingNotMeetingType = items?.filter((i) => i.type === EPreSelectedResource.MeetingType);
        if (notifyMeetingType && itemsPendingNotMeetingType?.length === 1) {
          removeItem(notifyMeetingType);
        }
      }
      if (notifyMeetingType) {
        const resourcesInMeetingType = notifyMeetingType.resources.filter((r) => r.id !== notifyInfo.resources[0].id);
        notifyMeetingType.resources = resourcesInMeetingType;
        updateItem(notifyMeetingType);
      }
      removeItem(notifyInfo);
      removeMultipleResources([notifyInfo.resources[0]]);
      if(selectedResource.length === 1 && selectedResource[0].id === notifyInfo.resources[0].id && location.pathname.includes(routes.selectedResources)) {
        navigate(routes.recommendation);
      }
    }
  };

  const onHandleRemoveItem = (notifyInfo: PreSelectedResourceInfo) => {
    if (notifyInfo) {
      saveSessionId(notifyInfo.resources[0].email?.toLowerCase(), "order_form_not_required");
      removeItem(notifyInfo);
      updateResourcePending(notifyInfo);
    }
  };

  return {
    openPlaceOrder,
    onHandleCancel,
    onHandleRemoveItem,
  };
};
